import type { BookData } from '../interfaces/book-data.interface';
import type { CollageData } from '../interfaces/collage-data.interface';
import { isDrawableGroup, type DrawableData, type DrawableGroupData } from '../interfaces/drawable-data.interface';
import type { PrintSizes } from '../interfaces/print-sizes.interface';
import type { SpreadData } from '../interfaces/spread-data.interface';
import { getCollageHorizontalOffset } from './spread-queries';

type SpreadLike = Pick<SpreadData, 'collages'>;

/** Pre-order over a drawable tree; `ancestors` — the group chain above the visited node, outermost first. */
export function forEachDrawableDeep(
  drawables: readonly DrawableData[],
  visit: (drawable: DrawableData, ancestors: readonly DrawableGroupData[]) => void,
): void {
  const walk = (items: readonly DrawableData[], ancestors: readonly DrawableGroupData[]): void => {
    for (const drawable of items) {
      visit(drawable, ancestors);
      if (isDrawableGroup(drawable)) walk(drawable.drawableObjects ?? [], [...ancestors, drawable]);
    }
  };

  walk(drawables, []);
}

/** First pre-order match at any depth — groups are tested like any other node. */
export function findDrawableDeep(
  drawables: readonly DrawableData[],
  match: (drawable: DrawableData) => boolean,
): DrawableData | undefined {
  for (const drawable of drawables) {
    if (match(drawable)) return drawable;
    if (isDrawableGroup(drawable)) {
      const found = findDrawableDeep(drawable.drawableObjects ?? [], match);
      if (found) return found;
    }
  }

  return undefined;
}

/**
 * Identity-preserving deep map: a subtree nothing in which was replaced keeps its object references,
 * and an untouched forest comes back as the SAME array — callers decide "did anything change" by
 * reference, so a rebuilt-but-equal tree would count as an edit (store history, change detection).
 */
export function mapDrawablesDeep(
  drawables: readonly DrawableData[],
  fn: (drawable: DrawableData, ancestors: readonly DrawableGroupData[]) => DrawableData,
): readonly DrawableData[] {
  const walk = (items: readonly DrawableData[], ancestors: readonly DrawableGroupData[]): readonly DrawableData[] => {
    const mapped = items.map(item => {
      const next = fn(item, ancestors);
      if (!isDrawableGroup(next)) return next;
      const children = next.drawableObjects ?? [];
      const inner = walk(children, [...ancestors, next]);
      return inner === children ? next : ({ ...next, drawableObjects: inner } as DrawableData);
    });

    return mapped.some((next, i) => next !== items[i]) ? mapped : items;
  };

  return walk(drawables, []);
}

/** The spread-level shape every role/photo stamp shares: same spread object back when fn changed nothing. */
export function mapSpreadDrawablesDeep<T extends SpreadLike>(
  spread: T,
  fn: (drawable: DrawableData, ancestors: readonly DrawableGroupData[]) => DrawableData,
): T {
  let changed = false;
  const collages = (spread.collages ?? []).map(collage => {
    const children = collage.drawableObjects ?? [];
    const inner = mapDrawablesDeep(children, fn);
    if (inner === children) return collage;
    changed = true;
    return { ...collage, drawableObjects: inner };
  });

  return changed ? ({ ...spread, collages } as T) : spread;
}

/** The node plus every descendant, pre-order — the canvas whole-group selection unit. */
export function subtreeDrawableIds(root: DrawableData): string[] {
  const ids = [root.id];
  if (isDrawableGroup(root)) forEachDrawableDeep(root.drawableObjects ?? [], d => ids.push(d.id));
  return ids;
}

/** Pre-order over the whole book, cover first — the container order project-text readers rely on. */
export function forEachBookDrawable(
  book: BookData,
  visit: (drawable: DrawableData, ancestors: readonly DrawableGroupData[]) => void,
): void {
  for (const container of [book.cover, ...(book.spreads ?? [])]) {
    for (const collage of container?.collages ?? []) forEachDrawableDeep(collage.drawableObjects ?? [], visit);
  }
}

/** First match across the book in `forEachBookDrawable` order. */
export function findBookDrawable(book: BookData, match: (drawable: DrawableData) => boolean): DrawableData | undefined {
  for (const container of [book.cover, ...(book.spreads ?? [])]) {
    for (const collage of container?.collages ?? []) {
      const found = findDrawableDeep(collage.drawableObjects ?? [], match);
      if (found) return found;
    }
  }

  return undefined;
}

export interface DrawableOrigin {
  readonly x: number;
  readonly y: number;
}

/**
 * Pre-order with each node's sheet-space origin: a collage starts at its horizontal print offset, and a
 * group's children live in the group's own coordinates — the origin accumulates every enclosing
 * container's top-left, so `origin + naturalAligns.container` is the node's absolute print position.
 */
export function forEachDrawableAtSpreadOrigin(
  collages: readonly CollageData[] | undefined,
  printSizes: PrintSizes,
  visit: (drawable: DrawableData, origin: DrawableOrigin) => void,
): void {
  const walk = (drawables: readonly DrawableData[], x: number, y: number): void => {
    for (const drawable of drawables) {
      visit(drawable, { x, y });
      if (isDrawableGroup(drawable)) {
        const rect = drawable.naturalAligns.container;
        walk(drawable.drawableObjects ?? [], x + rect.left, y + rect.top);
      }
    }
  };

  (collages ?? []).forEach((collage, index) =>
    walk(collage.drawableObjects ?? [], getCollageHorizontalOffset(printSizes, index), 0),
  );
}
